/* @jsxImportSource react */
'use client';

import { forwardRef, type MouseEvent, type SVGProps, useCallback } from 'react';

import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { useObservability } from '@opendoor/observability/slim';

import { type AnalyticsProps } from '../../helpers/analytics';
import { useImpressionRef } from '../../helpers/use-impression-ref';
import { cn } from '../../utils/cn';
import { type LinkRenderProps, useLinkRenderer } from '../../utils/link-provider';
import { type StripUndesiredProps } from '../../utils/strip-undesired-props';

/**
 * ExternalIcon - Visual indicator for links that open in a new tab/window.
 *
 * Use this as a child of Link to indicate external navigation:
 * ```tsx
 * <Link analyticsName="app-example" href="https://example.com" target="_blank" rel="noopener noreferrer">
 *   Visit Example <Link.ExternalIcon />
 * </Link>
 * ```
 */
const ExternalIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="link-external-icon"
    {...props}
  >
    <path d="M9 4H8.8C7.11984 4 6.27976 4 5.63803 4.32698C5.07354 4.6146 4.6146 5.07354 4.32698 5.63803C4 6.27976 4 7.11984 4 8.8V15.2C4 16.8802 4 17.7202 4.32698 18.362C4.6146 18.9265 5.07354 19.3854 5.63803 19.673C6.27976 20 7.11984 20 8.8 20H15.2C16.8802 20 17.7202 20 18.362 19.673C18.9265 19.3854 19.3854 18.9265 19.673 18.362C20 17.7202 20 16.8802 20 15.2V15" />
    <path d="M14 4H20M20 4V10M20 4L11 13" />
  </svg>
);

ExternalIcon.displayName = 'Link.ExternalIcon';

/** Link variant definitions mapping variant names to their Tailwind classes. */
export const ATOMS_LINK_VARIANTS = {
  variant: {
    inline: {
      classes:
        // text-od-content-accent-brand provides defensive color that won't be overridden by global `a` styles
        'text-od-content-accent-brand hover:text-od-content-hover-accent-brand underline underline-offset-[0.15em] decoration-[0.0625em] link-current transition-colors',
      description: 'Inline text link that flows with content',
    },
  },
} as const;

/** @internal */
export const ATOMS_LINK_DEFAULT_VARIANTS = {
  variant: 'inline',
} as const;

/** The single link style — `inline` is the only variant, so there's no prop. */
export function linkVariants() {
  return cn(ATOMS_LINK_VARIANTS.variant.inline.classes);
}

/**
 * Link component props.
 *
 * @example
 * ```tsx
 * <Link href="/docs">Learn more</Link>
 * <Link href="https://opendoor.com" target="_blank" rel="noopener noreferrer">
 *   Visit Opendoor <Link.ExternalIcon />
 * </Link>
 * <Link render={<RouterLink to="/dashboard" />}>Dashboard</Link>
 * ```
 */
export type LinkProps = StripUndesiredProps<useRender.ComponentProps<'a'>> &
  StripUndesiredProps<LinkRenderProps> &
  Omit<AnalyticsProps, 'analyticsName'> & {
    /**
     * Names this link in journey-service events. Convention:
     * `{appName}-{componentName}`.
     */
    analyticsName: string;
  };

/**
 * Link component for consistent inline text links.
 *
 * Supports composition via `render` prop for framework-specific routing:
 * - Without render: renders via LinkProvider's renderer (default anchor or configured renderer)
 * - With render: merges props onto the provided element with proper ref/event handling
 *
 * @example Basic usage
 * ```tsx
 * <Link analyticsName="app-docs" href="/docs">Learn more</Link>
 * ```
 *
 * @example External link with icon
 * ```tsx
 * <Link analyticsName="app-opendoor" href="https://opendoor.com" target="_blank" rel="noopener noreferrer">
 *   Visit Opendoor <Link.ExternalIcon />
 * </Link>
 * ```
 *
 * @example Composition with React Router
 * ```tsx
 * <Link analyticsName="app-dashboard" render={<RouterLink to="/dashboard" />}>Dashboard</Link>
 * ```
 */
const LinkBase = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { className, render, analyticsName, trackImpression, ...props },
  ref,
) {
  const renderLink = useLinkRenderer();
  const { trackJourneyEvent } = useObservability();
  const viewRef = useImpressionRef(
    Boolean(analyticsName && trackImpression),
    analyticsName,
    'linkViewEvent',
    ref,
  );

  // Fire analytics then let navigation proceed — no preventDefault. Composed
  // via mergeProps so the injected link renderer's own onClick still runs.
  const handleClick = useCallback(
    (_event: MouseEvent<HTMLAnchorElement>) => {
      if (analyticsName) {
        trackJourneyEvent('platform', 'linkClickEvent', { targetName: analyticsName });
      }
    },
    [analyticsName, trackJourneyEvent],
  );

  const defaultProps: useRender.ElementProps<'a'> = {
    className: cn(linkVariants(), 'group/link inline-flex items-center gap-[0.1875em]'),
    onClick: handleClick,
  };

  // When no `render` is supplied, default to the configured link renderer (see
  // RenderLink). useRender calls it with the merged props (ref included) and
  // inlines its element, so the anchor's type stays static — never a
  // context-provided component rendered as JSX.
  return useRender({
    render: render ?? renderLink,
    ref: viewRef,
    props: mergeProps<'a'>(defaultProps, props, { className }),
  });
});

LinkBase.displayName = 'Link';

/**
 * Anchor for consistent inline and standalone text links. Renders an
 * underlined brand-colored link.
 *
 * By default it renders through the app's `LinkProvider` (a plain `<a>` unless a
 * framework link is configured); pass a `render` element to delegate to a
 * router link while keeping ref/event handling intact. The `Link.ExternalIcon`
 * sub-component renders a trailing glyph for links that open in a new tab.
 *
 * @example
 * ```tsx
 * <Link analyticsName="app-docs" href="/docs">Learn more</Link>
 *
 * <Link analyticsName="app-opendoor" href="https://opendoor.com" target="_blank" rel="noopener noreferrer">
 *   Visit Opendoor <Link.ExternalIcon />
 * </Link>
 *
 * <Link analyticsName="app-dashboard" render={<RouterLink to="/dashboard" />}>Dashboard</Link>
 * ```
 */
export const Link = Object.assign(LinkBase, {
  ExternalIcon,
});
